import { useMemo, memo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Project, BudgetStatus } from '../../../shared/types';
import { TimeEntry } from '@/services/timeTracking';
import { formatCurrency } from '@/utils/currency';
import { calculateHourlyBudgetStatus, isExcludedBudgetDay } from '@/utils/budgetUtils';

interface ProjectBudgetChartProps {
  project: Project;
  timeEntries: TimeEntry[];
  getCurrentDuration: (entry: TimeEntry) => number;
  budgetWarningColor?: string;
}

interface DailyData {
  date: string;
  displayDate: string;
  worked: number;
  spent: number;
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  value: number;
  budget: number;
  isOverBudget: boolean; // Keep for backward compatibility
  budgetStatus?: BudgetStatus; // NEW: Three-state status
  deviationHours?: number; // NEW: Distance from valid range
  worked?: number;
  spent?: number;
  cumulativeSpent?: number;
  cumulativeHours?: number;
  revenue?: number; // Daily revenue for billable projects
  cumulativeRevenue?: number; // Cumulative revenue for billable projects
}

type Granularity = 'daily' | 'weekly' | 'monthly';

/**
 * Determine appropriate time granularity based on date range
 */
function determineGranularity(entries: TimeEntry[]): Granularity {
  if (entries.length === 0) return 'daily';

  const dates = entries.map(e => new Date(e.startTime).getTime());
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const daySpan = (maxDate - minDate) / (1000 * 60 * 60 * 24);

  if (daySpan <= 30) return 'daily';
  if (daySpan <= 90) return 'daily'; // Keep daily but will sample if needed
  if (daySpan <= 365) return 'weekly';
  return 'monthly';
}

/**
 * Get week identifier (YYYY-WW format)
 */
function getWeekIdentifier(date: Date): string {
  const year = date.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const daysSinceStart = Math.floor((date.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
  const weekNumber = Math.ceil((daysSinceStart + startOfYear.getDay() + 1) / 7);
  return `${year}-W${String(weekNumber).padStart(2, '0')}`;
}

/**
 * Get month identifier (YYYY-MM format)
 */
function getMonthIdentifier(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Aggregate time entries by date/week/month
 */
function aggregateTimeEntries(
  entries: TimeEntry[],
  project: Project,
  getCurrentDuration: (entry: TimeEntry) => number,
  granularity: Granularity
): Map<string, DailyData> {
  const aggregated = new Map<string, DailyData>();

  entries.forEach(entry => {
    const entryDate = new Date(entry.startTime);
    let key: string;
    let displayDate: string;

    if (granularity === 'weekly') {
      key = getWeekIdentifier(entryDate);
      const weekYear = key.split('-W')[0].slice(2);
      displayDate = `W${key.split('-W')[1]} '${weekYear}`;
    } else if (granularity === 'monthly') {
      key = getMonthIdentifier(entryDate);
      const monthDate = new Date(key + '-01');
      displayDate = monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    } else {
      key = entryDate.toISOString().split('T')[0];
      displayDate = entryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    }

    if (!aggregated.has(key)) {
      aggregated.set(key, {
        date: key,
        displayDate,
        worked: 0,
        spent: 0
      });
    }

    const data = aggregated.get(key)!;
    const hours = getCurrentDuration(entry) / 3600;
    data.worked += hours;

    // Calculate revenue/spending for billable projects
    if (project.billable) {
      let hourlyRate = project.hourlyRate || 0;
      if (entry.location && project.locations) {
        const locationConfig = project.locations.find(loc => loc.name === entry.location);
        if (locationConfig?.hourlyRate !== undefined) {
          hourlyRate = locationConfig.hourlyRate;
        }
      }
      data.spent += hours * hourlyRate;
    }
  });

  return aggregated;
}

/**
 * Calculate chart data for monetary budgets (cumulative spending)
 */
function calculateMonetaryBudgetData(
  project: Project,
  aggregatedData: Map<string, DailyData>
): ChartDataPoint[] {
  const budget = project.budget || 0;
  const sortedData = Array.from(aggregatedData.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));

  let cumulative = 0;
  const chartData: ChartDataPoint[] = sortedData.map(([, data]) => {
    cumulative += data.spent;
    return {
      date: data.date,
      displayDate: data.displayDate,
      value: cumulative,
      budget,
      isOverBudget: cumulative > budget,
      worked: data.worked,
      spent: data.spent,
      cumulativeSpent: cumulative
    };
  });

  return chartData;
}

/**
 * Calculate chart data for hourly budgets (daily/weekly/monthly hours)
 */
function calculateHourlyBudgetData(
  project: Project,
  aggregatedData: Map<string, DailyData>,
  granularity: Granularity
): ChartDataPoint[] {
  const dailyBudget = project.budget || 0;
  const toleranceHours = (project.budgetHourLimit || 60) / 60;

  // Adjust budget based on granularity
  let budget = dailyBudget;
  if (granularity === 'weekly') {
    budget = dailyBudget * 5;
  } else if (granularity === 'monthly') {
    budget = dailyBudget * 22;
  }

  const sortedData = Array.from(aggregatedData.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));

  let cumulativeRevenue = 0;
  const chartData: ChartDataPoint[] = sortedData.map(([, data]) => {
    const budgetStatus: BudgetStatus =
      granularity === 'daily' && isExcludedBudgetDay(data.date, project.budgetExcludedDays ?? [0, 6])
        ? 'in'
        : calculateHourlyBudgetStatus(project, data.worked, granularity);
    const isOverBudget = budgetStatus === 'over';

    // Calculate deviation from the tolerance boundary for tooltip display
    const scaleFactor = granularity === 'weekly' ? 5 : granularity === 'monthly' ? 22 : 1;
    const scaledTolerance = toleranceHours * scaleFactor;
    let deviationHours = 0;
    if (budgetStatus === 'over') {
      deviationHours = data.worked - (budget + scaledTolerance);
    } else if (budgetStatus === 'under') {
      deviationHours = (budget - scaledTolerance) - data.worked;
    }

    cumulativeRevenue += data.spent; // spent field contains revenue for billable projects

    return {
      date: data.date,
      displayDate: data.displayDate,
      value: data.worked,
      budget,
      isOverBudget, // Backward compatibility
      budgetStatus, // NEW
      deviationHours, // NEW
      worked: data.worked,
      revenue: data.spent,
      cumulativeRevenue
    };
  });

  return chartData;
}

/**
 * Calculate chart data for projects without budget (cumulative hours)
 */
function calculateWorkedHoursData(
  aggregatedData: Map<string, DailyData>
): ChartDataPoint[] {
  const sortedData = Array.from(aggregatedData.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));

  let cumulativeHours = 0;
  let cumulativeRevenue = 0;
  const chartData: ChartDataPoint[] = sortedData.map(([, data]) => {
    cumulativeHours += data.worked;
    cumulativeRevenue += data.spent; // spent field contains revenue for billable projects

    return {
      date: data.date,
      displayDate: data.displayDate,
      value: cumulativeHours,
      budget: 0,
      isOverBudget: false,
      budgetStatus: 'in', // NEW: No budget = always "in"
      deviationHours: 0, // NEW
      worked: data.worked,
      cumulativeHours,
      revenue: data.spent,
      cumulativeRevenue
    };
  });

  return chartData;
}

const ProjectBudgetChart = memo(function ProjectBudgetChart({
  project,
  timeEntries,
  budgetWarningColor: budgetWarningColorProp
}: ProjectBudgetChartProps) {
  const budgetWarningColor = budgetWarningColorProp || '#EF4444';

  // Calculate stable durations for completed entries only.
  // Keyed by a stable string so this memo never changes due to timer ticks.
  const completedEntriesKey = useMemo(() =>
    timeEntries
      .filter(e => e.endTime)
      .map(e => `${e.id}-${e.endTime}`)
      .sort()
      .join('|'),
    [timeEntries]
  );

  const stableDurations = useMemo(() => {
    const durations = new Map<string, number>();
    timeEntries.forEach(entry => {
      if (entry.endTime) {
        durations.set(entry.id,
          entry.duration ??
          Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000)
        );
      }
    });
    return durations;
    // Only recompute when the set of completed entries changes, not on every timer tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedEntriesKey]);

  const chartData = useMemo(() => {
    const projectEntries = timeEntries.filter(entry => entry.projectId === project.id);
    const granularity = determineGranularity(projectEntries);
    const hasBudget = project.billable && project.budget;

    // If no entries, return empty state data for all projects
    if (projectEntries.length === 0) {
      return {
        data: [],
        budget: hasBudget ? project.budget : undefined,
        budgetType: project.budgetType,
        currency: project.currency,
        isEmpty: true,
        granularity,
        hasBudget,
        billable: project.billable
      };
    }

    // Use stable duration calculator — only completed entries contribute to the chart.
    // Running entries (no endTime) return 0 and are effectively excluded.
    const stableGetDuration = (entry: TimeEntry) => stableDurations.get(entry.id) ?? 0;

    const aggregatedData = aggregateTimeEntries(
      projectEntries,
      project,
      stableGetDuration,
      granularity
    );

    let data: ChartDataPoint[];
    if (hasBudget && project.budgetType === 'monetary') {
      // Billable with monetary budget
      data = calculateMonetaryBudgetData(project, aggregatedData);
    } else if (hasBudget && project.budgetType === 'hours') {
      // Billable with hourly budget
      data = calculateHourlyBudgetData(project, aggregatedData, granularity);
    } else {
      // Non-billable or no budget - show cumulative hours
      data = calculateWorkedHoursData(aggregatedData);
    }

    return {
      data,
      budget: hasBudget ? project.budget : undefined,
      budgetType: project.budgetType,
      currency: project.currency,
      isEmpty: false,
      granularity,
      hasBudget,
      billable: project.billable
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, project.billable, project.budget, project.budgetType, project.hourlyRate, project.currency, project.locations, project.budgetExcludedDays, stableDurations]);

  if (!chartData) {
    return null;
  }

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      return (
        <div className="bg-white p-2 border border-gray-200 rounded-lg shadow-lg text-xs">
          <p className="font-medium text-gray-900">{data.displayDate}</p>
          {!chartData.hasBudget ? (
            // No budget - show cumulative hours and revenue for billable projects
            <>
              <p className="text-gray-600">
                Worked: {data.worked?.toFixed(1)}h
              </p>
              <p className="text-gray-600">
                Total: {data.cumulativeHours?.toFixed(1)}h
              </p>
              {chartData.billable && data.cumulativeRevenue !== undefined && data.cumulativeRevenue > 0 && (
                <p className="text-gray-600">
                  Revenue: {formatCurrency(data.cumulativeRevenue, chartData.currency)}
                </p>
              )}
            </>
          ) : chartData.budgetType === 'monetary' ? (
            // Monetary budget
            <>
              <p className="text-gray-600">
                Worked: {formatCurrency(data.spent || 0, chartData.currency)}
              </p>
              <p className="text-gray-600">
                Budget: {formatCurrency(chartData.budget || 0, chartData.currency)}
              </p>
              <p className="text-gray-600">
                Revenue: {formatCurrency(data.cumulativeSpent || 0, chartData.currency)}
              </p>
            </>
          ) : (
            // Hourly budget
            <>
              <p className="text-gray-600">
                Worked: {data.worked?.toFixed(1)}h
              </p>
              <p className="text-gray-600">
                Budget: {chartData.budget}h/{chartData.granularity === 'daily' ? 'day' : chartData.granularity === 'weekly' ? 'week' : 'month'} ± {(() => {
                  const toleranceHours = ((project.budgetHourLimit || 60) / 60);
                  if (chartData.granularity === 'weekly') return (toleranceHours * 5).toFixed(1);
                  if (chartData.granularity === 'monthly') return (toleranceHours * 22).toFixed(1);
                  return toleranceHours.toFixed(1);
                })()}h
              </p>
              {chartData.billable && data.cumulativeRevenue !== undefined && data.cumulativeRevenue > 0 && (
                <p className="text-gray-600">
                  Revenue: {formatCurrency(data.cumulativeRevenue, chartData.currency)}
                </p>
              )}
              {/* NEW: Status messages with deviation */}
              {data.budgetStatus === 'over' && data.deviationHours !== undefined && data.deviationHours > 0 && (
                <p className="font-medium text-xs mt-1 flex items-center gap-0.5" style={{ color: budgetWarningColor }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                  Over budget by {data.deviationHours.toFixed(1)}h
                </p>
              )}
              {data.budgetStatus === 'under' && data.deviationHours !== undefined && data.deviationHours > 0 && (
                <p className="font-medium text-xs mt-1 flex items-center gap-0.5" style={{ color: budgetWarningColor }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                  Under budget by {data.deviationHours.toFixed(1)}h
                </p>
              )}
              {data.budgetStatus === 'in' && (
                <p className="text-green-600 font-medium text-xs mt-1">✓ In budget</p>
              )}
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // Empty state - show for all projects without entries
  if (chartData.isEmpty) {
    return (
      <div className="w-full relative" style={{ height: '100px' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-gray-400">No entries yet</span>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart margin={{ top: 5, right: 5, left: -40, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis tick={{ fontSize: 9 }} stroke="#9CA3AF" />
            <YAxis
              tick={false}
              stroke="#9CA3AF"
              domain={[0, chartData.hasBudget && chartData.budget ? chartData.budget * 1.2 : 100]}
              width={40}
            />
            {/* Only show budget line if project has budget */}
            {chartData.hasBudget && chartData.budget && (
              <ReferenceLine
                y={chartData.budget}
                stroke="#10B981"
                strokeDasharray="5 5"
                strokeWidth={1.5}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const maxValue = Math.max(
    ...chartData.data.map(d => d.value),
    chartData.hasBudget && chartData.budget ? chartData.budget : 0
  );

  // Create dynamic gradient stops based on budget status for each data point
  const createGradientStops = () => {
    if (!chartData.hasBudget || chartData.data.length === 0) {
      // No budget or no data - use project color
      return [
        { offset: '5%', color: project.color || '#3B82F6', opacity: 0.8 },
        { offset: '95%', color: project.color || '#3B82F6', opacity: 0.1 }
      ];
    }

    // For hourly budgets, create gradient stops for each data point
    if (chartData.budgetType === 'hours') {
      const stops: Array<{ offset: string; color: string; opacity: number }> = [];
      const dataLength = chartData.data.length;

      chartData.data.forEach((point, index) => {
        // Calculate position as percentage (0% to 100% from top to bottom of chart)
        const position = dataLength > 1 ? (index / (dataLength - 1)) * 100 : 50;

        // Determine color based on budget status
        const color = (point.budgetStatus === 'over' || point.budgetStatus === 'under')
          ? budgetWarningColor // Red for out of budget
          : project.color || '#3B82F6'; // Project color for in budget

        // Add gradient stops for smooth transitions
        // Top stop (high opacity for area fill)
        if (index === 0) {
          stops.push({ offset: '0%', color, opacity: 0.8 });
        }

        // Middle stop at data point position
        stops.push({ offset: `${position}%`, color, opacity: 0.5 });

        // Bottom stop (low opacity)
        if (index === dataLength - 1) {
          stops.push({ offset: '100%', color, opacity: 0.1 });
        }
      });

      return stops;
    } else {
      // For monetary budgets, check if over budget
      const isOverBudget = chartData.data.some(d => d.isOverBudget);
      const color = isOverBudget ? budgetWarningColor : project.color || '#3B82F6';
      return [
        { offset: '5%', color, opacity: 0.8 },
        { offset: '95%', color, opacity: 0.1 }
      ];
    }
  };

  const gradientStops = createGradientStops();

  // Create horizontal gradient for stroke color (left to right based on data points)
  const createStrokeGradient = () => {
    if (!chartData.hasBudget || chartData.data.length === 0 || chartData.budgetType !== 'hours') {
      return null;
    }

    const stops: Array<{ offset: string; color: string }> = [];
    const dataLength = chartData.data.length;

    chartData.data.forEach((point, index) => {
      // Calculate horizontal position (0% = left, 100% = right)
      const position = dataLength > 1 ? (index / (dataLength - 1)) * 100 : 50;

      // Determine color based on budget status
      const color = (point.budgetStatus === 'over' || point.budgetStatus === 'under')
        ? budgetWarningColor
        : project.color || '#3B82F6';

      stops.push({ offset: `${position}%`, color });

      // Add intermediate stops for smoother transitions
      if (index < dataLength - 1) {
        const nextPoint = chartData.data[index + 1];
        const nextColor = (nextPoint.budgetStatus === 'over' || nextPoint.budgetStatus === 'under')
          ? budgetWarningColor
          : project.color || '#3B82F6';

        // Only add transition if colors differ
        if (color !== nextColor) {
          const midPosition = dataLength > 1 ? ((index + 0.5) / (dataLength - 1)) * 100 : 50;
          stops.push({ offset: `${midPosition}%`, color });
        }
      }
    });

    return stops;
  };

  const strokeGradientStops = createStrokeGradient();

  // Determine reference line color based on budget status
  const hasOutOfBudget = chartData.hasBudget && chartData.data.some(d =>
    d.budgetStatus === 'over' || d.budgetStatus === 'under'
  );

  return (
    <div className="w-full" style={{ height: '100px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData.data}
          margin={{ top: 5, right: 5, left: -40, bottom: 5 }}
        >
          <defs>
            {/* Vertical gradient for area fill (top to bottom) */}
            <linearGradient id={`gradient-${project.id}`} x1="0" y1="0" x2="0" y2="1">
              {gradientStops.map((stop, index) => (
                <stop
                  key={`fill-${index}`}
                  offset={stop.offset}
                  stopColor={stop.color}
                  stopOpacity={stop.opacity}
                />
              ))}
            </linearGradient>

            {/* Horizontal gradient for stroke (left to right) */}
            {strokeGradientStops && (
              <linearGradient id={`stroke-gradient-${project.id}`} x1="0" y1="0" x2="1" y2="0">
                {strokeGradientStops.map((stop, index) => (
                  <stop
                    key={`stroke-${index}`}
                    offset={stop.offset}
                    stopColor={stop.color}
                  />
                ))}
              </linearGradient>
            )}
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />

          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 9 }}
            stroke="#9CA3AF"
            interval="preserveStartEnd"
          />

          <YAxis
            tick={false}
            stroke="#9CA3AF"
            domain={[0, maxValue * 1.15]}
            width={40}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* Budget reference line - only show if project has budget */}
          {chartData.hasBudget && (
            <ReferenceLine
              y={chartData.budget}
              stroke={hasOutOfBudget ? budgetWarningColor : '#10B981'}
              strokeDasharray="5 5"
              strokeWidth={1.5}
            />
          )}

          <Area
            type="monotone"
            dataKey="value"
            stroke={strokeGradientStops ? `url(#stroke-gradient-${project.id})` : project.color || '#3B82F6'}
            strokeWidth={2}
            fill={`url(#gradient-${project.id})`}
            animationDuration={500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if any project field used by the chart changes,
  // or if the completed time entries for this project change.
  if (prevProps.project.id !== nextProps.project.id) return false;
  if (prevProps.project.budget !== nextProps.project.budget) return false;
  if (prevProps.project.budgetType !== nextProps.project.budgetType) return false;
  if (prevProps.project.budgetHourLimit !== nextProps.project.budgetHourLimit) return false;
  if (prevProps.project.billable !== nextProps.project.billable) return false;
  if (prevProps.project.hourlyRate !== nextProps.project.hourlyRate) return false;
  if (prevProps.project.currency !== nextProps.project.currency) return false;
  if (prevProps.project.color !== nextProps.project.color) return false;
  if (JSON.stringify(prevProps.project.locations) !== JSON.stringify(nextProps.project.locations)) return false;
  if (JSON.stringify(prevProps.project.budgetExcludedDays) !== JSON.stringify(nextProps.project.budgetExcludedDays)) return false;
  if (prevProps.budgetWarningColor !== nextProps.budgetWarningColor) return false;

  // Check if completed time entries for this project changed
  const prevCompleted = prevProps.timeEntries.filter(e => e.projectId === prevProps.project.id && e.endTime);
  const nextCompleted = nextProps.timeEntries.filter(e => e.projectId === nextProps.project.id && e.endTime);

  if (prevCompleted.length !== nextCompleted.length) return false;

  // Check if any entry IDs or endTimes changed
  const prevIds = prevCompleted.map(e => e.id + e.endTime).sort().join(',');
  const nextIds = nextCompleted.map(e => e.id + e.endTime).sort().join(',');

  return prevIds === nextIds;
});

export default ProjectBudgetChart;
